import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Import Firebase services
import { auth, db, storage, functions } from '../firebase'; // Ensure this path is correct
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { ref as firebaseStorageRef, uploadBytesResumable as firebaseUploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

// Import date-fns format function - not strictly needed for V2 data sending but useful for display if ever
// import { format } from 'date-fns';
import { isValid, parse } from 'date-fns'; // For client-side validation of constructed date

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers, faCalendarAlt, faPhone, faMapMarkerAlt, faCity, faMapPin, faMap,
    faImage, faSpinner, faCheckCircle, faExclamationTriangle, faSave,
    faTrashAlt
} from '@fortawesome/free-solid-svg-icons';

// Callable Function Reference for V2
const updateFamilyV2Callable = httpsCallable(functions, 'updateFamilyV2');

// Helper functions for anniversary dropdowns (copied from AddFamilyPage)
const getMonths = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
        months.push({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) });
    }
    return months;
};

const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && parseInt(year, 10) >= 1800) ? parseInt(year, 10) : 2000; // Default to a leap year for day count flexibility
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};

const getYears = () => {
    const years = [{ value: '', label: 'Year (Optional)' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 100; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};


function EditFamilyPage() {
    const { familyId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    // Form State
    const [surname, setSurname] = useState('');
    // NEW Anniversary state variables
    const [selectedAnniversaryMonth, setSelectedAnniversaryMonth] = useState('');
    const [selectedAnniversaryDay, setSelectedAnniversaryDay] = useState('');
    const [selectedAnniversaryYear, setSelectedAnniversaryYear] = useState('');

    const [phone, setPhone] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');

    // Image State
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [existingPhotoPath, setExistingPhotoPath] = useState(null); // Store the canonical path from DB
    const [isPhotoRemoved, setIsPhotoRemoved] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Component State
    const [isLoading, setIsLoading] = useState(false);
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [currentUserOrgId, setCurrentUserOrgId] = useState(null);

    // --- Effect to get User's Org ID ---
    useEffect(() => {
        let isMounted = true;
        const unsubscribeAuth = auth.onAuthStateChanged(user => {
            if (!isMounted) return;
            if (user) {
                user.getIdTokenResult()
                    .then(idTokenResult => {
                        if (!isMounted) return;
                        const orgId = idTokenResult.claims.orgId;
                        if (!orgId) {
                             setMessage({ type: 'danger', text: 'Error: Organization ID not found.' });
                             console.error("User is missing orgId claim.");
                             setLoadingInitialData(false); // Stop loading if critical info missing
                        }
                        setCurrentUserOrgId(orgId);
                    })
                    .catch(err => {
                        if (!isMounted) return;
                        console.error("Error fetching user claims:", err);
                        setMessage({ type: 'danger', text: 'Error fetching user data.' });
                        setLoadingInitialData(false);
                    });
            } else {
                 setMessage({ type: 'warning', text: 'Please log in.' });
                 setLoadingInitialData(false);
                 navigate('/login');
            }
        });
        return () => {
            isMounted = false;
            unsubscribeAuth();
        };
    }, [navigate]);

    // --- Effect to Fetch Existing Family Data ---
    const fetchFamilyData = useCallback(async () => {
        if (!familyId || !currentUserOrgId) {
             if (!familyId && !loadingInitialData) { // Avoid setting message if still loading orgId
                 setMessage({ type: 'danger', text: 'Error: Family ID not provided.' });
             }
             return;
        }

        console.log(`Fetching data for family ${familyId} in org ${currentUserOrgId}`);
        setLoadingInitialData(true);
        setMessage({ type: '', text: '' });
        const familyDocRef = doc(db, "family", familyId);

        try {
            const docSnap = await getDoc(familyDocRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.orgId !== currentUserOrgId) {
                     console.error(`Permission Denied: User org ${currentUserOrgId} does not match family org ${data.orgId}`);
                     setMessage({ type: 'danger', text: 'Error: You do not have permission to edit this family.' });
                     setLoadingInitialData(false);
                     return;
                }

                setSurname(data.surname || '');
                setPhone(data.phone || '');
                setStreet(data.street || '');
                setCity(data.city || '');
                setState(data.state || '');
                setZipcode(data.zipcode || '');

                // --- Updated Anniversary Population Logic ---
                let initialMonth = '';
                let initialDay = '';
                let initialYear = '';

                if (data.anniversary && data.anniversary instanceof Timestamp) {
                    try {
                        const anniversaryDate = data.anniversary.toDate();
                        initialMonth = String(anniversaryDate.getMonth() + 1);
                        initialDay = String(anniversaryDate.getDate());
                        initialYear = String(anniversaryDate.getFullYear()); // Default to timestamp's year

                        // Override with V2 string fields if they exist, especially for year handling ("no year")
                        if (data.anniversaryMonth) {
                            initialMonth = String(data.anniversaryMonth).trim();
                        }
                        if (data.anniversaryDay) {
                            initialDay = String(data.anniversaryDay).trim();
                        }

                        // If V2 year field is explicitly null or empty, it means "no year"
                        if (data.anniversaryYear === null || (typeof data.anniversaryYear === 'string' && data.anniversaryYear.trim() === '')) {
                            initialYear = '';
                        } else if (data.anniversaryYear) { // If V2 year field has a value, it's the source of truth
                            initialYear = String(data.anniversaryYear).trim();
                        }
                        // If anniversaryYear is undefined, initialYear (from Timestamp) is used.

                    } catch (e) {
                        console.error("Error parsing anniversary Timestamp for dropdowns:", e);
                        // Fallback to V2 string fields if Timestamp parsing fails or if Timestamp doesn't exist
                        initialMonth = data.anniversaryMonth ? String(data.anniversaryMonth).trim() : '';
                        initialDay = data.anniversaryDay ? String(data.anniversaryDay).trim() : '';
                        initialYear = data.anniversaryYear ? String(data.anniversaryYear).trim() : '';
                    }
                } else {
                    // If no 'anniversary' Timestamp, rely solely on V2 string fields
                    initialMonth = data.anniversaryMonth ? String(data.anniversaryMonth).trim() : '';
                    initialDay = data.anniversaryDay ? String(data.anniversaryDay).trim() : '';
                    initialYear = data.anniversaryYear ? String(data.anniversaryYear).trim() : '';
                }

                // If month or day ended up empty, year should also be empty to be consistent
                if (!initialMonth || !initialDay) {
                    initialYear = '';
                }

                setSelectedAnniversaryMonth(initialMonth);
                setSelectedAnniversaryDay(initialDay);
                setSelectedAnniversaryYear(initialYear);
                // --- End of Updated Anniversary Population Logic ---

                setExistingPhotoPath(data.photo || null);

                if (data.photo) {
                    try {
                        const photoRef = firebaseStorageRef(storage, data.photo);
                        const url = await getDownloadURL(photoRef);
                        setImagePreviewUrl(url);
                    } catch (urlError) {
                         console.error("Error getting initial photo URL:", urlError);
                         setImagePreviewUrl(null);
                    }
                } else {
                     setImagePreviewUrl(null);
                }

            } else {
                console.log("No such family document!");
                setMessage({ type: 'danger', text: 'Family not found.' });
            }
        } catch (error) {
            console.error("Error getting family document:", error);
            setMessage({ type: 'danger', text: `Error loading family data: ${error.message}` });
        } finally {
            setLoadingInitialData(false);
        }
    }, [familyId, currentUserOrgId, db, storage]);

    useEffect(() => {
        if (currentUserOrgId && familyId) {
            fetchFamilyData();
        }
    }, [fetchFamilyData, currentUserOrgId, familyId]);


    // --- Effect to cleanup image preview URL (for blob URLs) ---
    useEffect(() => {
        return () => {
            if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
        };
    }, [imagePreviewUrl]);


    // --- Input Handlers ---
    const handleFileChange = (event) => {
        setMessage({ type: '', text: '' });
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImageFile(file);
            setIsPhotoRemoved(false);

            const newPreviewUrl = URL.createObjectURL(file);
            if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
            setImagePreviewUrl(newPreviewUrl);
        } else {
            if (file) {
                setMessage({ type: 'warning', text: 'Please select a valid image file.' });
            }
        }
        if (event.target) event.target.value = null;
    };

     const handleRemovePhoto = () => {
        setMessage({ type: '', text: '' });
        setImageFile(null);
        if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(imagePreviewUrl);
        }
        setImagePreviewUrl(null);
        setIsPhotoRemoved(true);
        if (fileInputRef.current) {
             fileInputRef.current.value = null;
        }
    };

    // --- Validation Functions ---
    const validateForm = () => {
        if (!surname.trim()) {
            setMessage({ type: 'warning', text: 'Family Surname is required.' }); return false;
        }
        const phoneDigits = phone.trim().replace(/\D/g, "");
        if (phone.trim() && (phoneDigits.length < 7 || phoneDigits.length > 15) ) {
             setMessage({ type: 'warning', text: 'Phone number, if provided, must contain between 7 and 15 digits.' }); return false;
        }
        if (state.trim() && !/^[A-Za-z]{2}$/.test(state.trim())) {
             setMessage({ type: 'warning', text: 'State, if provided, must be a 2-letter abbreviation.' }); return false;
        }
        if (zipcode.trim() && !/^\d{5}(-\d{4})?$/.test(zipcode.trim())) {
             setMessage({ type: 'warning', text: 'Zipcode, if provided, must be a valid 5 or 9 digit format.' }); return false;
        }

        // Anniversary Validation for V2
        const month = selectedAnniversaryMonth;
        const day = selectedAnniversaryDay;
        const year = selectedAnniversaryYear;

        if ((month && !day) || (!month && day && month !== "")) {
            setMessage({ type: 'warning', text: 'If selecting an anniversary month or day, both are required.' }); return false;
        }
        if (year && (!month || !day)) {
            setMessage({ type: 'warning', text: 'If selecting an anniversary year, month and day are also required.' }); return false;
        }
        if (month && day) {
            const yearForValidation = year || 2000;
            const dateStr = `${yearForValidation}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            if (!isValid(parse(dateStr, 'yyyy-MM-dd', new Date()))) {
                 setMessage({ type: 'warning', text: `Anniversary date ${month}/${day}${year ? '/'+year : ''} is not a valid calendar date.` }); return false;
            }
        }
        return true;
    };


    // --- Form Submission ---
    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage({ type: '', text: '' });

        if (!validateForm()) {
            return;
        }
        if (!currentUserOrgId || !familyId) {
            setMessage({ type: 'danger', text: 'Cannot update family: Missing required IDs.' }); return;
        }

        setIsLoading(true);
        setUploadProgress(0);
        let photoInstruction = existingPhotoPath;

        try {
            // Handle Photo Update for V2
            if (imageFile) {
                setMessage({ type: 'info', text: 'Uploading new photo...' });
                const fileExtension = imageFile.name.split('.').pop();
                const tempGCSPath = `${currentUserOrgId}/photos/${uuidv4()}.${fileExtension}`;

                const actualStorageRef = firebaseStorageRef(storage, tempGCSPath);
                const uploadTask = firebaseUploadBytesResumable(actualStorageRef, imageFile);

                photoInstruction = await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
                        (error) => {
                            console.error("New photo upload error:", error);
                            reject(new Error(`New photo upload error: ${error.message}`));
                        },
                        () => resolve(tempGCSPath)
                    );
                });
                 setMessage({ type: 'info', text: 'New photo uploaded. Updating family...' });
            } else if (isPhotoRemoved) {
                photoInstruction = null;
                setMessage({ type: 'info', text: 'Photo marked for removal. Updating family...' });
            }

             const updatedFamilyData = {
                familyId: familyId,
                surname: surname.trim(),
                street: street.trim() || null,
                city: city.trim() || null,
                state: state.trim().toUpperCase() || null,
                zipcode: zipcode.trim() || null,
                phone: phone.trim() || null,
                anniversaryMonth: selectedAnniversaryMonth ? String(selectedAnniversaryMonth).trim() : null,
                anniversaryDay: selectedAnniversaryDay ? String(selectedAnniversaryDay).trim() : null,
                anniversaryYear: selectedAnniversaryYear ? String(selectedAnniversaryYear).trim() : null,
                photo: photoInstruction,
             };

            console.log("Calling updateFamilyV2 with data:", JSON.stringify(updatedFamilyData, null, 2));
            const result = await updateFamilyV2Callable(updatedFamilyData);

            if (result?.data?.success) {
                setMessage({ type: 'success', text: result.data.message || 'Family updated successfully!' });
                if (typeof result.data.photoPath === 'string' || result.data.photoPath === null) {
                    const newPhotoPath = result.data.photoPath || null;
                    setExistingPhotoPath(newPhotoPath);
                    if (newPhotoPath) {
                        if (imageFile) {
                             if (imagePreviewUrl && imagePreviewUrl.startsWith('blob:')) {
                                URL.revokeObjectURL(imagePreviewUrl);
                             }
                             try {
                                const finalUrl = await getDownloadURL(firebaseStorageRef(storage, newPhotoPath));
                                setImagePreviewUrl(finalUrl);
                             } catch (e) { console.error("Could not get new photo URL post-update", e); setImagePreviewUrl(null); }
                        } else {
                             try {
                                const finalUrl = await getDownloadURL(firebaseStorageRef(storage, newPhotoPath));
                                setImagePreviewUrl(finalUrl);
                             } catch (e) { console.error("Could not get existing photo URL post-update", e); setImagePreviewUrl(null); }
                        }
                    } else {
                        setImagePreviewUrl(null);
                    }
                }
                setImageFile(null);
                setIsPhotoRemoved(false);

                setTimeout(() => navigate(`/families/${familyId}`), 2000);
            } else {
                throw new Error(result?.data?.message || 'Failed to update family on the server.');
            }
        } catch (error) {
            console.error("Error submitting family update:", error);
            let displayErrorMessage = error.message || 'An unexpected error occurred.';
            if (error.code && error.message && typeof error.details !== 'undefined') {
                displayErrorMessage = error.message;
            }
            setMessage({ type: 'danger', text: displayErrorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    // Memoize dropdown options
    const availableMonths = React.useMemo(() => getMonths(), []);
    const availableDays = React.useMemo(() => getDaysInMonth(selectedAnniversaryMonth, selectedAnniversaryYear), [selectedAnniversaryMonth, selectedAnniversaryYear]);
    const availableYears = React.useMemo(() => getYears(), []);


    if (loadingInitialData) {
         return ( <div className="container text-center mt-5"><FontAwesomeIcon icon={faSpinner} spin size="3x" /><p className="mt-2">Loading Family Data...</p></div> );
    }

     if (message.type === 'danger' && (!currentUserOrgId || (familyId && !surname && !loadingInitialData))) {
         return (
            <div className="container mt-4">
                 <nav aria-label="breadcrumb" className="mb-3">
                    <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
                        <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                        <li className="breadcrumb-item"><Link to="/families">Families</Link></li>
                        <li className="breadcrumb-item active" aria-current="page">Edit Family Error</li>
                    </ol>
                 </nav>
                 <div className="alert alert-danger">{message.text || 'Failed to load family data or missing permissions.'}</div>
                 <Link to="/families" className="btn btn-secondary">Back to Families</Link>
            </div>
         );
     }


    return (
        <div className="container mt-4 mb-5">
            <nav aria-label="breadcrumb" className="mb-3">
                 <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
                    <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                    <li className="breadcrumb-item"><Link to="/families">Families</Link></li>
                    <li className="breadcrumb-item"><Link to={`/families/${familyId}`}>Family Detail</Link></li>
                    <li className="breadcrumb-item active" aria-current="page">Edit Family</li>
                </ol>
            </nav>

            <div className="row justify-content-center">
                <div className="col-md-10 col-lg-8">
                    <div className="card shadow-sm">
                        <div className="card-body p-4">
                            <h2 className="card-title text-center mb-4">Edit Family Details</h2>

                            <form onSubmit={handleSubmit}>
                                {message.text && (
                                    <div className={`alert alert-${message.type || 'info'} d-flex flex-column`} role="alert">
                                        <div className="d-flex align-items-center">
                                            {message.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="me-2 flex-shrink-0" />}
                                            {(message.type === 'danger' || message.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 flex-shrink-0" />}
                                            {message.type === 'info' && isLoading && <FontAwesomeIcon icon={faSpinner} spin className="me-2 flex-shrink-0" /> }
                                            <span>{message.text}</span>
                                        </div>
                                        {isLoading && uploadProgress > 0 && imageFile && message.type === 'info' && (
                                            <div className="progress mt-2" style={{height: '5px', width: '100%'}}>
                                                <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${uploadProgress}%` }} aria-valuenow={uploadProgress} aria-valuemin="0" aria-valuemax="100"></div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label htmlFor="surname" className="form-label">Family Surname *</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><FontAwesomeIcon icon={faUsers} /></span>
                                        <input type="text" className="form-control" id="surname" value={surname} onChange={(e) => setSurname(e.target.value)} required disabled={isLoading || loadingInitialData} />
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="phone" className="form-label">Home Phone</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><FontAwesomeIcon icon={faPhone} /></span>
                                        <input type="tel" className="form-control" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={isLoading || loadingInitialData} placeholder="(Optional)" />
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="street" className="form-label">Street Address</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                                        <input type="text" className="form-control" id="street" value={street} onChange={(e) => setStreet(e.target.value)} disabled={isLoading || loadingInitialData} placeholder="(Optional)" />
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label htmlFor="city" className="form-label">City</label>
                                         <div className="input-group">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faCity} /></span>
                                            <input type="text" className="form-control" id="city" value={city} onChange={(e) => setCity(e.target.value)} disabled={isLoading || loadingInitialData} placeholder="(Optional)" />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label htmlFor="state" className="form-label">State</label>
                                         <div className="input-group">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faMap} /></span>
                                            <input type="text" className="form-control text-uppercase" id="state" placeholder="e.g. NC" value={state} onChange={(e) => setState(e.target.value)} maxLength="2" disabled={isLoading || loadingInitialData} />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label htmlFor="zipcode" className="form-label">Zip Code</label>
                                         <div className="input-group">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faMapPin} /></span>
                                            <input type="text" className="form-control" id="zipcode" value={zipcode} onChange={(e) => setZipcode(e.target.value)} disabled={isLoading || loadingInitialData} placeholder="e.g. 12345" />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Anniversary (Optional)</label>
                                    <div className="row g-2">
                                        <div className="col-sm-5">
                                            <div className="input-group">
                                                 <span className="input-group-text"><FontAwesomeIcon icon={faCalendarAlt} style={{fontSize: '0.8rem'}}/></span>
                                                <select
                                                    id="anniversaryMonthEdit"
                                                    className="form-select"
                                                    value={selectedAnniversaryMonth}
                                                    onChange={(e) => {
                                                        setSelectedAnniversaryMonth(e.target.value);
                                                        if (!e.target.value) setSelectedAnniversaryDay('');
                                                    }}
                                                    disabled={isLoading || loadingInitialData}
                                                    aria-label="Anniversary Month"
                                                >
                                                    <option value="">Month</option>
                                                    {availableMonths.map(m => <option key={`edit-month-${m.value}`} value={m.value}>{m.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <select
                                                id="anniversaryDayEdit"
                                                className="form-select"
                                                value={selectedAnniversaryDay}
                                                onChange={(e) => setSelectedAnniversaryDay(e.target.value)}
                                                disabled={isLoading || loadingInitialData || !selectedAnniversaryMonth}
                                                aria-label="Anniversary Day"
                                            >
                                                <option value="">Day</option>
                                                {availableDays.map(d => <option key={`edit-day-${d.value}`} value={d.value}>{d.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-sm-4">
                                            <select
                                                id="anniversaryYearEdit"
                                                className="form-select"
                                                value={selectedAnniversaryYear}
                                                onChange={(e) => setSelectedAnniversaryYear(e.target.value)}
                                                disabled={isLoading || loadingInitialData}
                                                aria-label="Anniversary Year"
                                            >
                                                {availableYears.map(y => <option key={`edit-year-${y.value}`} value={y.value}>{y.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>


                                <hr className="my-4"/>
                                <div className="mb-3 text-center">
                                    <label className="form-label d-block mb-3">Family Photo</label>
                                    {imagePreviewUrl && !isPhotoRemoved ? (
                                        <div className="mb-3">
                                            <img src={imagePreviewUrl} alt="Family preview" style={{ maxHeight: '200px', maxWidth: '90%', height: 'auto', border: '1px solid #ccc', padding: '4px', borderRadius: '4px' }} />
                                        </div>
                                    ) : isPhotoRemoved ? (
                                        <div className="mb-3 text-muted fst-italic">(Photo will be removed on update)</div>
                                    ) : existingPhotoPath ? (
                                        <div className="mb-3 text-muted fst-italic">(Current photo will be kept unless changed or removed)</div>
                                    ) : (
                                         <div className="mb-3 text-muted fst-italic">(No photo currently set)</div>
                                    )}

                                    <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} disabled={isLoading || loadingInitialData} />

                                    <button type="button" className="btn btn-outline-primary me-2" onClick={() => fileInputRef.current?.click()} disabled={isLoading || loadingInitialData} >
                                        <FontAwesomeIcon icon={faImage} className="me-1" /> {imagePreviewUrl || existingPhotoPath ? 'Change Photo...' : 'Choose Photo...'}
                                    </button>

                                    { (imagePreviewUrl || existingPhotoPath) && !isPhotoRemoved && (
                                         <button type="button" className="btn btn-outline-danger ms-2" onClick={handleRemovePhoto} disabled={isLoading || loadingInitialData} >
                                             <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Remove Photo
                                        </button>
                                    )}
                                    {isPhotoRemoved && (
                                        <span className="ms-2 text-info fst-italic">
                                            Photo marked for removal. Choose a new photo or save to remove.
                                        </span>
                                    )}
                                </div>
                                <hr className="my-4"/>

                                <div className="d-grid">
                                    <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading || loadingInitialData || !currentUserOrgId || !familyId } >
                                        {isLoading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                {uploadProgress > 0 && imageFile ? `Uploading ${uploadProgress}%` : 'Updating...'}
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faSave} className="me-1" /> Update Family
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditFamilyPage;
